/**
 * Aanmaken en wijzigen van één toetsingsregel.
 *
 * De drie dingen die dit formulier moet afdwingen staan alle drie in
 * `intake-velden.ts` uitgelegd:
 *  1. `veld` is een keuzelijst uit `SubsidieIntakeData`, geen vrije tekst — een
 *     regel op een onbekend veld doet niets of maakt een permanent datagat.
 *  2. de operatorkeuze volgt het type van het veld — `gte` op een tekstveld is
 *     altijd onwaar.
 *  3. `waarde` krijgt ook bij is_true / is_false / is_not_null een echte
 *     jsonb-waarde, want de kolom is NOT NULL.
 */
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Info } from 'lucide-react';
import type { SubsidieRegel } from '@/hooks/useSubsidieRegelingen';
import type { RegelInvoer } from '@/hooks/useSubsidieBeheer';
import {
  INTAKE_VELDEN,
  OPERATOR_LABELS,
  VELD_GROEPEN,
  controleerRegel,
  onbekendVeldUitleg,
  operatorenVoorVeld,
  vindIntakeVeld,
  waardeVormVoor,
  WAARDE_ZONDER_INVOER,
  type RegelOperator,
  type WaardeInvoer,
} from './intake-velden';

/**
 * Zet een opgeslagen jsonb-waarde terug om in de tekst die het formulier toont.
 * De database bewaart getallen als getal, lijsten als array en `between` als
 * tweedelige array.
 */
function waardeAlsInvoer(waarde: unknown): WaardeInvoer {
  if (Array.isArray(waarde)) {
    if (waarde.length === 2 && waarde.every(d => typeof d === 'number')) {
      return { tekst: String(waarde[0]), tot: String(waarde[1]) };
    }
    return { tekst: waarde.map(d => String(d)).join(', ') };
  }
  if (waarde === null || waarde === undefined) return {};
  return { tekst: String(waarde) };
}

export function RegelDialog({
  open,
  onOpenChange,
  regelingId,
  regel,
  volgendeSortOrder,
  onBewaar,
  isBezig,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  regelingId: string;
  /** `null` = nieuwe regel. */
  regel: SubsidieRegel | null;
  volgendeSortOrder: number;
  /**
   * Mag deze gebruiker het intake-veld en de vergelijking wijzigen? Migratie
   * 083 zet die twee op slot voor wie geen admin is: de rekenkern matcht er
   * letterlijk op, en een afwijking geeft geen fout maar een toets die stil
   * wordt overgeslagen. De waarde, de teksten en de volgorde blijven wél open;
   * dat is het gemeten onderhoud.
   */
  onBewaar: (invoer: { id?: string; velden: RegelInvoer }) => void;
  isBezig: boolean;
}) {
  const [veld, setVeld] = useState('');
  const [operator, setOperator] = useState<string>('');
  const [invoer, setInvoer] = useState<WaardeInvoer>({});
  const [verplicht, setVerplicht] = useState(true);
  const [beschrijving, setBeschrijving] = useState('');
  const [foutmelding, setFoutmelding] = useState('');
  const [groep, setGroep] = useState('');
  const [sortOrder, setSortOrder] = useState(0);

  useEffect(() => {
    if (!open) return;
    setVeld(regel?.veld ?? '');
    setOperator(regel?.operator ?? '');
    setInvoer(regel ? waardeAlsInvoer(regel.waarde) : {});
    setVerplicht(regel?.verplicht ?? true);
    setBeschrijving(regel?.beschrijving ?? '');
    setFoutmelding(regel?.foutmelding ?? '');
    setGroep(regel?.groep ?? '');
    setSortOrder(regel?.sort_order ?? volgendeSortOrder);
  }, [open, regel, volgendeSortOrder]);

  /**
   * Bij een NIEUWE regel staat alles open: er is geen OLD om tegen te
   * vergelijken en de INSERT-policy laat de drie CRM-rollen door. Het slot
   * geldt alleen bij het wijzigen van een bestaande regel — precies zoals de
   * trigger.
   */

  const gekozenVeld = vindIntakeVeld(veld);
  const toegestaneOperatoren = useMemo(() => operatorenVoorVeld(veld), [veld]);
  const vorm = gekozenVeld && operator
    ? waardeVormVoor(gekozenVeld.type, operator as RegelOperator)
    : null;

  const controle = useMemo(
    () => controleerRegel({ veld, operator, verplicht, beschrijving, invoer }),
    [veld, operator, verplicht, beschrijving, invoer],
  );

  // De regel zoals hij in de database staat kan op een veld staan dat de engine
  // niet kent. Dat mag niet stil blijven; zie `onbekendVeldUitleg`.
  const erfeniswaarschuwing = regel && !vindIntakeVeld(regel.veld)
    ? onbekendVeldUitleg(regel.veld, regel.verplicht)
    : null;

  function kiesVeld(nieuw: string) {
    setVeld(nieuw);
    const opties = operatorenVoorVeld(nieuw);
    // Past de gekozen operator niet meer bij het nieuwe veld, dan valt hij weg.
    // Dat is precies het geval dat `gte` op een tekstveld voorkomt.
    if (!opties.includes(operator as RegelOperator)) {
      setOperator(opties[0] ?? '');
      setInvoer({});
    }
  }

  const kanOpslaan = controle.fouten.length === 0 && !isBezig;

  function opslaan() {
    if (!kanOpslaan || controle.waarde === undefined) return;
    onBewaar({
      id: regel?.id,
      velden: {
        regeling_id: regelingId,
        veld,
        operator,
        waarde: controle.waarde,
        verplicht,
        beschrijving: beschrijving.trim(),
        foutmelding: foutmelding.trim() === '' ? null : foutmelding.trim(),
        groep: groep.trim() === '' ? null : groep.trim(),
        sort_order: sortOrder,
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{regel ? 'Regel wijzigen' : 'Nieuwe regel'}</DialogTitle>
          <DialogDescription>
            Een regel toetst één veld uit de subsidie-intake. Alleen velden die het intakeformulier
            daadwerkelijk invult staan in de lijst — een regel op een ander veld kan de engine niet lezen.
          </DialogDescription>
        </DialogHeader>

        {erfeniswaarschuwing && (
          <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{erfeniswaarschuwing}</span>
          </div>
        )}

        <p className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Na "Opslaan" zie je eerst wat deze wijziging met de lopende dossiers doet — welk dossier van
            welk bedrag naar welk bedrag gaat. Verschuift er niets, dan slaat hij meteen op.
          </span>
        </p>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Intake-veld</Label>
            <Select value={veld} onValueChange={kiesVeld}>
              <SelectTrigger><SelectValue placeholder="Kies een veld..." /></SelectTrigger>
              <SelectContent>
                {VELD_GROEPEN.map(g => (
                  <SelectGroup key={g}>
                    <SelectLabel>{g}</SelectLabel>
                    {INTAKE_VELDEN.filter(v => v.groep === g).map(v => (
                      <SelectItem key={v.veld} value={v.veld}>
                        {v.label}{v.eenheid ? ` (${v.eenheid})` : ''}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            {gekozenVeld && (
              <p className="text-xs text-muted-foreground">
                Type: {gekozenVeld.type}
                {gekozenVeld.keuzes ? ` — mogelijke waarden: ${gekozenVeld.keuzes.join(', ')}` : ''}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Vergelijking</Label>
              <Select
                value={operator}
                onValueChange={v => { setOperator(v); setInvoer({}); }}
                disabled={!gekozenVeld}
              >
                <SelectTrigger><SelectValue placeholder={gekozenVeld ? 'Kies...' : 'Kies eerst een veld'} /></SelectTrigger>
                <SelectContent>
                  {toegestaneOperatoren.map(op => (
                    <SelectItem key={op} value={op}>{OPERATOR_LABELS[op]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {gekozenVeld && (
                <p className="text-xs text-muted-foreground">
                  Alleen de vergelijkingen die de engine op een veld van dit type kan uitvoeren.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Waarde</Label>
              {vorm === 'geen' && (
                <p className="flex items-start gap-1 text-xs text-muted-foreground pt-2">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  Deze vergelijking heeft geen waarde nodig. De regel krijgt automatisch{' '}
                  <code className="mx-1">
                    {String(WAARDE_ZONDER_INVOER[operator as 'is_true' | 'is_false' | 'is_not_null'])}
                  </code>{' '}
                  mee, omdat de kolom niet leeg mag zijn.
                </p>
              )}
              {vorm === 'bereik' && (
                <div className="flex items-center gap-2">
                  <Input
                    value={invoer.tekst ?? ''}
                    onChange={e => setInvoer(v => ({ ...v, tekst: e.target.value }))}
                    placeholder="van"
                  />
                  <span className="text-muted-foreground">t/m</span>
                  <Input
                    value={invoer.tot ?? ''}
                    onChange={e => setInvoer(v => ({ ...v, tot: e.target.value }))}
                    placeholder="tot en met"
                  />
                </div>
              )}
              {(vorm === 'getal' || vorm === 'tekst' || vorm === 'lijst') && (
                <Input
                  value={invoer.tekst ?? ''}
                  onChange={e => setInvoer(v => ({ ...v, tekst: e.target.value }))}
                  placeholder={
                    vorm === 'lijst'
                      ? 'meerdere waarden, gescheiden door komma’s'
                      : vorm === 'getal' ? 'bijv. 2500' : 'bijv. bedrijventerrein'
                  }
                />
              )}
              {!vorm && <p className="text-xs text-muted-foreground pt-2">Kies eerst veld en vergelijking.</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Omschrijving</Label>
            <Input
              value={beschrijving}
              onChange={e => setBeschrijving(e.target.value)}
              placeholder="bijv. Minimale investering EUR 2.500"
            />
          </div>

          <div className="space-y-2">
            <Label>Foutmelding (optioneel)</Label>
            <Input
              value={foutmelding}
              onChange={e => setFoutmelding(e.target.value)}
              placeholder="Wat de gebruiker ziet als de regel niet slaagt"
            />
          </div>

          <div className="grid grid-cols-3 gap-4 items-end">
            <div className="space-y-2">
              <Label>Groep (optioneel)</Label>
              <Input value={groep} onChange={e => setGroep(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Volgorde</Label>
              <Input
                type="number"
                value={sortOrder}
                onChange={e => setSortOrder(Number(e.target.value) || 0)}
              />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Switch checked={verplicht} onCheckedChange={setVerplicht} />
              <Label className="cursor-pointer" onClick={() => setVerplicht(!verplicht)}>Verplicht</Label>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            {verplicht
              ? 'Verplicht: is dit veld niet ingevuld, dan blijft de regeling op "gedeeltelijk" staan tot het wél is ingevuld.'
              : 'Niet verplicht: is dit veld niet ingevuld, dan slaat de engine de toets over.'}
          </p>

          {controle.waarschuwingen.map(w => (
            <div key={w} className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{w}</span>
            </div>
          ))}
          {controle.fouten.map(f => (
            <div key={f} className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{f}</span>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button onClick={opslaan} disabled={!kanOpslaan}>Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
